import { sortByKey } from '@semios/app-platform-common'
import type { TPermission } from '@semios/app-platform-value-type-definitions'
import { Checkbox } from 'components/Checkbox/Checkbox'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { checkAuthorization } from 'utils/checkAuthorization'
import { constantCaseify } from 'utils/constantCaseify'
import { getBlocksFromProperties } from 'utils/getBlocksFromProperties'
import { getPointsByValuesCurrentKey } from 'utils/getPointsByValuesCurrentKey'
import type { TOptionsWithExternalVendors } from 'utils/makeExternalVendorMultiSelectProps'
import { makeExternalVendorMultiSelectProps } from 'utils/makeExternalVendorMultiSelectProps'
import type { AlertTypeId, Rule } from '../../../_types'
import { AlertSection } from '../../../components/AlertSection'
import { ErrorTextWrapper } from '../../../components/ErrorTextWrapper'
import { AlertsSettings } from '../../../settings/alertSettings'
import { alertRulesObjectFromArray } from '../_utils/alertRulesObjectFromArray'
import { updateAlertRules } from '../_utils/updateAlertRules'
import { RadioWrapper } from './RadioWrapper/RadioWrapper'

const CREATE_alertTypeId_ALERT = (alertTypeId: AlertTypeId) => {
  if (alertTypeId === 'weatherForecastAlert') return 'CREATE_WEATHER_FORECAST_ALERT'

  return `CREATE_${constantCaseify(alertTypeId)}_ALERT`
}

const multiSelectDataBuilder = ({
  includeBlocks,
  includeStations,
  properties,
  stationTypeKey,
}: {
  includeBlocks: boolean
  includeStations: boolean
  properties: TProperty[]
  stationTypeKey: string
}) => {
  let stationsToReturn: TOptionsWithExternalVendors[] = []
  let blocksToReturn: TOptionsWithExternalVendors[] = []

  if (includeStations) {
    stationsToReturn = getPointsByValuesCurrentKey({
      properties: properties,
      key: stationTypeKey,
    })
      .map((station) => ({
        label: station.name,
        value: `${station.propertyId}&${station.geom}`,
        group: station.propertyName,
        id: station.geom,
        externalVendorNames: station.externalVendorNames,
      }))
      .sort(sortByKey('label'))
  }

  if (includeBlocks) {
    blocksToReturn = getBlocksFromProperties({ properties })
      .map((block) => ({
        label: block.name,
        value: `${block.propertyId}&${block.blockId}`,
        group: block.propertyName,
        id: String(block.blockId),
      }))
      .sort(sortByKey('label'))
  }

  return [...stationsToReturn, ...blocksToReturn].sort(sortByKey('group'))
}

export const Coverage = ({
  alertTypeId,
  coverageBlocks,
  coverageIsValid,
  coverageProperties,
  coverageStations,
  coverageType,
  insectId,
  rules,
  setCoverageBlocks,
  setCoverageIsValid,
  setCoverageProperties,
  setCoverageStations,
  setCoverageType,
  setRules,
  triedToSubmit,
}: {
  alertTypeId: AlertTypeId
  coverageBlocks: string[]
  coverageIsValid: boolean
  coverageProperties: number[]
  coverageStations: string[]
  coverageType: string
  insectId: number | null
  rules: Rule[]
  setCoverageBlocks: Dispatch<SetStateAction<string[]>>
  setCoverageIsValid: Dispatch<SetStateAction<boolean>>
  setCoverageProperties: Dispatch<SetStateAction<number[]>>
  setCoverageStations: Dispatch<SetStateAction<string[]>>
  setCoverageType: Dispatch<SetStateAction<string>>
  setRules: Dispatch<SetStateAction<Rule[]>>
  triedToSubmit: boolean
}) => {
  const showAllDevicesRule = AlertsSettings.includeAllDevices[alertTypeId]
  // includeAllDevices-handling
  const { includeAllDevices: includeAllDevicesRule } = alertRulesObjectFromArray(rules)

  // because alert rule values are strings, we need to check if the string is 'true' for includeAllDevices
  const includeAllDevices =
    includeAllDevicesRule && includeAllDevicesRule.value === 'true' ? includeAllDevicesRule.value : false

  const typeIsAll = coverageType === 'all'
  const typeIsProperties = coverageType === 'properties'
  const typeIsStationsOrBlocks = coverageType === 'stationsOrBlocks'
  const stationTypeKey = AlertsSettings.stationTypeKey[alertTypeId]

  useEffect(() => {
    if (coverageType === 'all') setCoverageIsValid(true)

    if (coverageType === 'stationsOrBlocks')
      setCoverageIsValid(!!coverageStations.length || !!coverageBlocks.length)

    if (coverageType === 'properties') setCoverageIsValid(!!coverageProperties.length)
  }, [coverageProperties, coverageStations, coverageType])

  const getAllWording = () => {
    switch (true) {
      case alertTypeId === 'trapCatches':
        return translate.phrases.banyanApp('Traps Across All Properties')

      case includeAllDevices === 'true':
        return translate.phrases.banyanApp('Stations/Blocks Across All Properties')

      case !!AlertsSettings.allowCoverageStations[alertTypeId]:
        return translate.phrases.banyanApp('Stations Across All Properties')

      case !!AlertsSettings.allowCoverageBlocks[alertTypeId]:
        return translate.phrases.banyanApp('Blocks Across All Properties')

      default:
        return translate.phrases.banyanApp('Stations Across All Properties')
    }
  }

  const getHelpText = () => {
    switch (coverageType) {
      case 'properties':
        return translate.phrases.banyanApp('Please select at least one of your properties')

      case 'stationsOrBlocks':
        if (includeAllDevices)
          return translate.phrases.banyanApp('Please select at least one of your stations/blocks')

        return translate.phrases.banyanApp('Please select at least one of your stations')

      default:
        return ''
    }
  }

  let validateStatusError
  let helpText = ''

  if (!coverageIsValid && triedToSubmit && coverageType !== 'all') {
    helpText = getHelpText()

    validateStatusError = true
  }

  const properties = fieldAssetStore.useSelector((s) => s?.properties ?? {})

  const filteredPropertiesByPermission = Object.values(properties).filter((property: TProperty) => {
    let pestsPermission: string | null = null

    if (alertTypeId === 'trapCatches') pestsPermission = `VIEW_PESTS_TRAP_CATCHES_ID_${insectId}`

    if (alertTypeId === 'degreeDays') pestsPermission = `VIEW_PESTS_DEGREE_DAYS_ID_${insectId}`

    if (pestsPermission !== null) {
      return (
        checkAuthorization({
          permission: pestsPermission as TPermission,
          entity: property.propertyId,
        }) &&
        checkAuthorization({
          permission: CREATE_alertTypeId_ALERT(alertTypeId) as TPermission,
          entity: property.propertyId,
        })
      )
    }

    return checkAuthorization({
      permission: CREATE_alertTypeId_ALERT(alertTypeId) as TPermission,
      entity: property.propertyId,
    })
  })

  const propertiesData = filteredPropertiesByPermission
    .map((property) => ({
      label: property.propertyName,
      value: property.propertyId.toString(),
    }))
    .sort(sortByKey('label'))

  const getSelectedData = (options: TOptions[], coverage: string[]) => {
    const selectedData = options.filter((option) => coverage.includes(option.value as string))

    return selectedData
  }

  return (
    <AlertSection title={translate.phrases.banyanApp('Coverage')}>
      <div css={{ display: 'grid', gridTemplateColumns: '100%', gridGap: 24 }}>
        <RadioWrapper checked={typeIsAll} onClick={() => setCoverageType('all')} title={getAllWording()} />
        {/* PROPERTIES */}
        {!!AlertsSettings.allowCoverageProperties[alertTypeId] && (
          <RadioWrapper
            checked={typeIsProperties}
            onClick={() => setCoverageType('properties')}
            title={translate.phrases.banyanApp('Specific Properties')}
          >
            {typeIsProperties && (
              <MultiSelect
                data={propertiesData}
                placeholder={typeIsProperties ? translate.phrases.banyanApp('Select Properties') : ''}
                onChange={(selectedValues: TOptions[]) => {
                  setCoverageProperties(selectedValues.map((option) => +option.value))

                  setCoverageStations([])

                  setCoverageBlocks([])
                }}
                selectedData={
                  typeIsProperties ? getSelectedData(propertiesData, coverageProperties.map(String)) : []
                }
                disabled={!typeIsProperties}
                error={validateStatusError && typeIsProperties}
              />
            )}
          </RadioWrapper>
        )}
        {/* STATIONS/BLOCKS/ALL-DEVICES */}
        {(() => {
          /**
           * we have to merge stations and blocks in the same dropdown. This can be a
           * bit confusing, but luckily station geoms are very long—50 character hex
           * strings—and block ids are comparatively short integers. The keys in the
           * tree follow the pattern "propertyId-stationGeom" or "propertyId-blockId"
           * so we can just assume from those. Note this only works assuming geoms for
           * stations and blockIds for blocks, and blockIds to be short, so this will
           * need to be changed if that reality doesn't hold anymore
           */
          const stationsOrBlocksOnChange = (newStationsOrBlocks: TOptions[]) => {
            const { newBlocks, newStations } = newStationsOrBlocks.reduce(
              (a, b) => {
                if ((b.value as string).split('&')[1].length > 30) a.newStations.push(b.value as string)
                else a.newBlocks.push(b.value as string)

                return a
              },
              {
                newBlocks: [] as string[],
                newStations: [] as string[],
              },
            )

            setCoverageBlocks(newBlocks)

            setCoverageStations(newStations)

            setCoverageProperties([])
          }

          if (includeAllDevices) {
            const stationOrBlocksData = multiSelectDataBuilder({
              includeBlocks: true,
              includeStations: true,
              properties: filteredPropertiesByPermission,
              stationTypeKey,
            })

            return (
              <RadioWrapper
                checked={typeIsStationsOrBlocks}
                onClick={() => setCoverageType('stationsOrBlocks')}
                title={translate.phrases.banyanApp('Specific Stations/Blocks')}
              >
                {typeIsStationsOrBlocks && (
                  <MultiSelect
                    data={stationOrBlocksData}
                    {...makeExternalVendorMultiSelectProps({ data: stationOrBlocksData })}
                    placeholder={
                      typeIsStationsOrBlocks ? translate.phrases.banyanApp('Select stations/blocks') : ''
                    }
                    onChange={(selectedValues: TOptions[]) => {
                      stationsOrBlocksOnChange(selectedValues)
                    }}
                    selectedData={
                      typeIsStationsOrBlocks
                        ? getSelectedData(stationOrBlocksData, [...coverageStations, ...coverageBlocks])
                        : []
                    }
                    groupOptions={true}
                    disabled={!typeIsStationsOrBlocks}
                    error={validateStatusError && typeIsStationsOrBlocks}
                  />
                )}
              </RadioWrapper>
            )
          }

          if (!!AlertsSettings.allowCoverageStations[alertTypeId]) {
            const stationOrBlocksData = multiSelectDataBuilder({
              includeBlocks: false,
              includeStations: true,
              properties: filteredPropertiesByPermission,
              stationTypeKey,
            })

            return (
              <RadioWrapper
                checked={typeIsStationsOrBlocks}
                onClick={() => setCoverageType('stationsOrBlocks')}
                title={translate.phrases.banyanApp('Specific Stations')}
              >
                {typeIsStationsOrBlocks && (
                  <MultiSelect
                    data={stationOrBlocksData}
                    {...makeExternalVendorMultiSelectProps({ data: stationOrBlocksData })}
                    placeholder={typeIsStationsOrBlocks ? translate.phrases.banyanApp('Select stations') : ''}
                    onChange={(selectedValues: TOptions[]) => {
                      stationsOrBlocksOnChange(selectedValues)
                    }}
                    selectedData={
                      typeIsStationsOrBlocks ? getSelectedData(stationOrBlocksData, coverageStations) : []
                    }
                    groupOptions={true}
                    disabled={!typeIsStationsOrBlocks}
                    error={validateStatusError && typeIsStationsOrBlocks}
                  />
                )}
              </RadioWrapper>
            )
          }

          if (!!AlertsSettings.allowCoverageBlocks[alertTypeId]) {
            const stationOrBlocksData = multiSelectDataBuilder({
              includeBlocks: true,
              includeStations: false,
              properties: filteredPropertiesByPermission,
              stationTypeKey,
            })

            return (
              <RadioWrapper
                checked={typeIsStationsOrBlocks}
                onClick={() => setCoverageType('stationsOrBlocks')}
                title={translate.phrases.banyanApp('Specific Blocks')}
              >
                {typeIsStationsOrBlocks && (
                  <MultiSelect
                    data={stationOrBlocksData}
                    placeholder={typeIsStationsOrBlocks ? translate.phrases.banyanApp('Select blocks') : ''}
                    onChange={(selectedValues: TOptions[]) => {
                      stationsOrBlocksOnChange(selectedValues)
                    }}
                    selectedData={
                      typeIsStationsOrBlocks ? getSelectedData(stationOrBlocksData, coverageBlocks) : []
                    }
                    groupOptions={true}
                    disabled={!typeIsStationsOrBlocks}
                    error={validateStatusError && typeIsStationsOrBlocks}
                  />
                )}
              </RadioWrapper>
            )
          }

          // in cases with nothing more granular than property—e.g. trapCatches—return null here
          return null
        })()}

        {showAllDevicesRule && (
          <Checkbox
            checked={!!includeAllDevices}
            onChange={() =>
              updateAlertRules({
                rules,
                setRules,
                // rule values are always strings, so let's make that boolean a string
                rulesToChange: {
                  includeAllDevices: { value: `${!includeAllDevices}`, operator: '=' },
                },
              })
            }
            label={translate.phrases.banyanApp('Include all devices')}
          />
        )}
        {helpText && <ErrorTextWrapper>{helpText}</ErrorTextWrapper>}
      </div>
    </AlertSection>
  )
}
